import { useEffect, useRef } from 'react'

/** `selectionchange` fires on every caret move; wait for the selection to settle. */
export const SELECTION_DEBOUNCE_MS = 300

const isTextField = (element) =>
  Boolean(element) && (
    element.tagName === 'TEXTAREA' ||
    (element.tagName === 'INPUT' && typeof element.selectionStart === 'number')
  )

/**
 * The text currently selected anywhere in MIC *outside* the widget (spec §5).
 * `window.getSelection()` cannot see inside <input>/<textarea>, so a focused foreign field is
 * read through its own selectionStart/selectionEnd instead.
 *
 * @param {Element|null} widgetEl the widget's root; selections inside it are ignored
 * @returns {string} raw selected text, '' when nothing usable is selected
 */
export const readSelection = (widgetEl) => {
  const active = document.activeElement
  if (isTextField(active)) {
    if (widgetEl && widgetEl.contains(active)) return ''
    return active.value.substring(active.selectionStart, active.selectionEnd)
  }

  const selection = window.getSelection ? window.getSelection() : null
  if (!selection || !selection.anchorNode) return ''
  if (widgetEl && widgetEl.contains(selection.anchorNode)) return ''
  return selection.toString()
}

/**
 * Fills the translator with what the user selects in MIC while the widget is open.
 * Only fills — never translates (spec §2). Replaces the text; accumulation is not wanted.
 *
 * @param {object} params
 * @param {boolean} params.enabled `status === 'open'` and not translating
 * @param {React.RefObject<Element>} params.widgetRef the widget's root, to ignore its own selections
 * @param {(text: string) => void} params.onCapture receives the trimmed, non-blank text
 */
const useSelectionCapture = ({ enabled, widgetRef, onCapture }) => {
  const capture = useRef(onCapture)
  capture.current = onCapture

  useEffect(() => {
    if (!enabled) return

    let timer = null
    const onSelectionChange = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const text = readSelection(widgetRef.current).trim()
        if (text !== '') capture.current(text)
      }, SELECTION_DEBOUNCE_MS)
    }

    document.addEventListener('selectionchange', onSelectionChange)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('selectionchange', onSelectionChange)
    }
  }, [enabled, widgetRef])
}

export default useSelectionCapture
